import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Disclosure } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { fetchStats } from '../api'
import { RegisterModal } from '@components/registration/RegisterModal'

function Wordmark() {
  return (
    <Link
      to="/"
      className="group inline-flex items-center gap-2 text-lg font-bold tracking-tight text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent rounded"
    >
      <span>SportsMeld</span>
      <span
        aria-hidden="true"
        className="inline-block h-1.5 w-1.5 rounded-full bg-dash-accent transition-transform duration-200 group-hover:scale-125"
      />
    </Link>
  )
}

function StatusDot() {
  // The "online" dot is a live/success signal, so it stays green (data semantics).
  const { data, isSuccess } = useQuery({
    queryKey: ['landing-status'],
    queryFn: fetchStats,
    refetchInterval: 30000,
  })
  const online = isSuccess && !data?.maintenance

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-dash-secondary" aria-live="polite">
      <span className="relative flex h-2 w-2">
        {online && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-75" />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            online ? 'bg-[#22C55E]' : 'bg-dash-muted'
          }`}
        />
      </span>
      <span className="hidden sm:inline">{online ? 'API online' : 'Checking…'}</span>
    </span>
  )
}

export function Navbar() {
  const [showRegister, setShowRegister] = useState(false)
  const navigate = useNavigate()

  const navLinks = (
    <>
      <Link
        to="/docs"
        className="text-sm text-dash-secondary transition-colors hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent rounded px-1 py-0.5"
      >
        Docs
      </Link>
      <button
        type="button"
        onClick={() => {
          navigate('/')
          setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 50)
        }}
        className="text-sm text-dash-secondary transition-colors hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent rounded px-1 py-0.5"
      >
        Pricing
      </button>
    </>
  )

  return (
    <>
      <Disclosure
        as="header"
        className="sticky top-0 z-50 border-b border-dash-border/70 bg-dash-bg/80 backdrop-blur"
      >
        {({ open }) => (
          <>
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-6">
                <Wordmark />
                <div className="hidden items-center gap-5 md:flex">{navLinks}</div>
              </div>

              <div className="hidden items-center gap-4 md:flex">
                <StatusDot />
                <a
                  href="/admin/login"
                  className="text-sm text-dash-secondary transition-colors hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent rounded px-1 py-0.5"
                >
                  Admin &rarr;
                </a>
                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  className="rounded-lg bg-gradient-to-r from-dash-accent to-dash-accent-2 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dash-bg"
                >
                  Get API key
                </button>
              </div>

              {/* Mobile hamburger */}
              <div className="flex items-center gap-3 md:hidden">
                <StatusDot />
                <Disclosure.Button
                  className="inline-flex items-center justify-center rounded-lg p-2 text-dash-secondary transition-colors hover:bg-dash-surface hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent"
                  aria-label="Toggle navigation menu"
                >
                  {open ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </nav>

            <Disclosure.Panel className="border-t border-dash-border/70 bg-dash-bg md:hidden">
              <div className="flex flex-col gap-1 px-4 py-4">
                <Link
                  to="/docs"
                  className="rounded-lg px-3 py-2 text-sm text-dash-secondary transition-colors hover:bg-dash-surface hover:text-dash-text"
                >
                  Docs
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/')
                    setTimeout(
                      () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }),
                      50,
                    )
                  }}
                  className="rounded-lg px-3 py-2 text-left text-sm text-dash-secondary transition-colors hover:bg-dash-surface hover:text-dash-text"
                >
                  Pricing
                </button>
                <a
                  href="/admin/login"
                  className="rounded-lg px-3 py-2 text-sm text-dash-secondary transition-colors hover:bg-dash-surface hover:text-dash-text"
                >
                  Admin &rarr;
                </a>
                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  className="mt-2 rounded-lg bg-gradient-to-r from-dash-accent to-dash-accent-2 px-4 py-2.5 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  Get API key
                </button>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} redirectTo="/admin" />}
    </>
  )
}
